import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { API_PREFIX, STORAGE_KEYS } from "@/shared/constants";
import type { ApiResponse } from "@/shared/types";
import { handleApiError } from "@/shared/utils/error";

// Axios 인스턴스 생성
export const http = axios.create({
  baseURL: API_PREFIX,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 토큰 재발급 상태 관리
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

/**
 * 토큰 재발급 대기 중인 요청들 처리
 */
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * 로컬 스토리지 초기화
 */
const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_INFO);
};

/**
 * 토큰 재발급 시도
 */
const refreshAccessToken = async (
  accessToken: string,
  refreshToken: string,
) => {
  const response = await axios.post(
    `${API_PREFIX}/auth/retoken`,
    { accessToken, refreshToken },
    { headers: { "Content-Type": "application/json" } },
  );

  const { content } = response.data;

  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, content.accessToken);
  if (content.refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, content.refreshToken);
  }
  localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(content));

  return content.accessToken;
};

// 요청 인터셉터
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// 응답 인터셉터
http.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    return response;
  },
  async (error: AxiosError<ApiResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // 401 Unauthorized 처리
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 이미 토큰 재발급 중이면 대기 큐에 추가
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return http(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      // refresh token이 없으면 로그인 페이지로 이동
      if (!accessToken || !refreshToken) {
        clearAuthData();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        // 토큰 재발급
        const newAccessToken = await refreshAccessToken(
          accessToken,
          refreshToken,
        );

        // 대기 중인 요청들 처리
        processQueue(null, newAccessToken);

        // 원래 요청 재시도
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        return http(originalRequest);
      } catch (refreshError) {
        // 토큰 재발급 실패 - 로그아웃
        processQueue(refreshError as Error, null);
        clearAuthData();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 에러 메시지 표시 (401은 제외, 자동 로그아웃되므로)
    if (error.response?.status !== 401) {
      handleApiError(error);
    }

    return Promise.reject(error);
  },
);
